import { Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../environments/environment';

export interface MsalUserData {
	account: {
		name?: string;
		username?: string;
		homeAccountId?: string;
		localAccountId?: string;
	};
	accessToken: string;
	idToken: string;
	scopes: string[];
	expiresOn: Date;
	idTokenClaims?: any;
}

@Injectable({
	providedIn: 'root'
})
export class MsalAuthService {
	private currentUserSubject = new BehaviorSubject<AccountInfo | null>(null);
	private userClaimsSubject = new BehaviorSubject<any>(null);
	private accessTokenSubject = new BehaviorSubject<string | null>(null);
	private initialized = false;

	public currentUser$ = this.currentUserSubject.asObservable();
	public userClaims$ = this.userClaimsSubject.asObservable();
	public accessToken$ = this.accessTokenSubject.asObservable();

	constructor(
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService
	) {
		// Don't initialize immediately - wait for explicit initialization
		this.setupBroadcastListener();
	}

	// New method to explicitly initialize the service
	async initialize() {
		if (this.initialized) {
			return;
		}

		await this.msalService.initialize();
		this.initializeUser();
		this.initialized = true;
	}

	private setupBroadcastListener() {
		// Listen for interaction status changes
		this.msalBroadcastService.inProgress$
			.pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
			.subscribe(() => {
				if (this.initialized) {
					this.updateCurrentUser();
				}
			});
	}

	private initializeUser() {
		// Check if user is already logged in
		const activeAccount = this.msalService.instance.getActiveAccount();
		if (activeAccount) {
			this.currentUserSubject.next(activeAccount);
			this.loadUserClaims();
		}
	}

	private updateCurrentUser() {
		const activeAccount = this.msalService.instance.getActiveAccount();
		this.currentUserSubject.next(activeAccount);

		if (activeAccount) {
			this.loadUserClaims();
		}
	}

	private loadUserClaims() {
		const account = this.msalService.instance.getActiveAccount();
		if (account) {
			// Try to get claims from cache or acquire new token
			this.msalService.instance.acquireTokenSilent({
				scopes: [environment.azureClient],
				account: account
			}).then(response => {
				if (response.idTokenClaims) {
					this.userClaimsSubject.next(response.idTokenClaims);
				}
				this.accessTokenSubject.next(response.accessToken);
			});
		}
	}

	// Get current user
	getCurrentUser(): AccountInfo | null {
		return this.currentUserSubject.value;
	}

	// Check if user is logged in
	isLoggedIn(): boolean {
		return this.getCurrentUser() !== null;
	}

	// Acquire token for specific scopes
	async acquireToken(scopes: string[] = [environment.azureClient]): Promise<string | null> {
		try {
			if (!this.initialized) {
				await this.initialize();
			}

			const account = this.msalService.instance.getActiveAccount();
			if (!account) {
				return null;
			}

			const response = await this.msalService.instance.acquireTokenSilent({
				scopes: scopes,
				account: account
			});

			this.accessTokenSubject.next(response.accessToken);
			return response.accessToken;
		} catch (error) {
			return null;
		}
	}

	// Login with redirect
	loginWithRedirect(scopes: string[] = [environment.azureClient]): void {
		this.msalService.loginRedirect({
			scopes: scopes,
			redirectStartPage: window.location.origin + '/redirect'
		});
	}
} 
